import { EventsKey } from 'ol/events';
import { getHeight, getWidth } from 'ol/extent';
import TileLayer from 'ol/layer/Tile';
import OLMap from 'ol/Map';
import { unByKey } from 'ol/Observable';
import Projection from 'ol/proj/Projection';
import TileArcGISRest, { Options as TileArcGISRestOptions } from 'ol/source/TileArcGISRest';
import { TileCoord } from 'ol/tilecoord';

export type ArcGISRequestParameters = Readonly<Record<string, string | number | boolean | null | undefined>>;

interface ArcGISUrlParts {
    url: string;
    params: URLSearchParams;
}

export interface ArcGISMapRenderingMetadata {
    singleFusedMapCache?: boolean;
    capabilities?: string;
    maxImageWidth?: number;
    maxImageHeight?: number;
}

class ViewResolutionArcGISTileSource extends TileArcGISRest {
    private viewResolution: number | undefined;
    private viewListeners: EventsKey[] = [];
    private mapListeners: EventsKey[] = [];
    private readonly maxImageWidth: number;
    private readonly maxImageHeight: number;

    constructor(options: TileArcGISRestOptions, map: OLMap, metadata: ArcGISMapRenderingMetadata) {
        super(options);
        this.maxImageWidth = this.getImageLimit(metadata.maxImageWidth);
        this.maxImageHeight = this.getImageLimit(metadata.maxImageHeight);
        const updateResolution = (): void => {
            const view = map.getView();
            const resolution = view.getResolution();
            const moving = view.getAnimating() || view.getInteracting();
            const exportResolution = moving && resolution !== this.viewResolution ? undefined : resolution;
            if (exportResolution !== this.viewResolution) {
                this.viewResolution = exportResolution;
                this.changed();
            }
        };
        const bindView = (): void => {
            unByKey(this.viewListeners);
            const view = map.getView();
            this.viewListeners = [view.on('change:resolution', updateResolution), view.on('change', updateResolution)];
            updateResolution();
        };
        bindView();
        this.mapListeners.push(
            map.on('change:view', bindView),
            map.getLayers().on('remove', (event) => {
                const layer: unknown = event.element;
                if (layer instanceof TileLayer && layer.getSource() === this) {
                    this.clearViewListeners();
                }
            }),
        );
    }

    protected getKey(): string {
        return `${super.getKey()}/view-resolution:${this.viewResolution}`;
    }

    public tileUrlFunction(tileCoord: TileCoord, pixelRatio: number, projection: Projection): string | undefined {
        const url = super.tileUrlFunction(tileCoord, pixelRatio, projection);
        const resolution = this.viewResolution;
        if (!url || resolution === undefined || !Number.isFinite(resolution) || resolution <= 0) {
            return url;
        }
        const tileGrid = this.getTileGridForProjection(projection);
        if (tileCoord[0] !== tileGrid.getZForResolution(resolution)) {
            return url;
        }
        const extent = tileGrid.getTileCoordExtent(tileCoord);
        const tilePixelRatio = this.getTilePixelRatio(pixelRatio);
        const width = Math.max(1, Math.round((getWidth(extent) / resolution) * tilePixelRatio));
        const height = Math.max(1, Math.round((getHeight(extent) / resolution) * tilePixelRatio));
        const sizeRatio = Math.min(1, this.maxImageWidth / width, this.maxImageHeight / height);
        const dpi = Number(parseArcGISUrl(url).params.get('DPI'));
        return buildArcGISRequestUrl(url, '', {
            SIZE: `${Math.max(1, Math.floor(width * sizeRatio))},${Math.max(1, Math.floor(height * sizeRatio))}`,
            DPI: sizeRatio < 1 ? Math.max(1, Math.round(dpi * sizeRatio)) : undefined,
        });
    }

    private getImageLimit(value: number | undefined): number {
        return value !== undefined && Number.isFinite(value) && value >= 1 ? Math.floor(value) : Infinity;
    }

    private clearViewListeners(): void {
        unByKey(this.viewListeners);
        unByKey(this.mapListeners);
        this.viewListeners = [];
        this.mapListeners = [];
    }

    public disposeInternal(): void {
        this.clearViewListeners();
        super.disposeInternal();
    }
}

const IMAGE_PARAMETER_NAMES = new Set([
    'f',
    'format',
    'transparent',
    'layers',
    'bbox',
    'bboxsr',
    'imagesr',
    'size',
    'dpi',
]);

function parseArcGISUrl(value: string): ArcGISUrlParts {
    const trimmedUrl = value.trim();
    const fragmentIndex = trimmedUrl.indexOf('#');
    const url = fragmentIndex === -1 ? trimmedUrl : trimmedUrl.slice(0, fragmentIndex);
    const queryIndex = url.indexOf('?');
    const baseUrl = queryIndex === -1 ? url : url.slice(0, queryIndex);

    return {
        url: baseUrl.replace(/\/+$/, ''),
        params: new URLSearchParams(queryIndex === -1 ? '' : url.slice(queryIndex + 1)),
    };
}

function setArcGISParameters(params: URLSearchParams, parameters: ArcGISRequestParameters): void {
    for (const [name, value] of Object.entries(parameters)) {
        if (value === undefined) {
            continue;
        }

        const matchingNames: string[] = [];
        params.forEach((_value, key) => {
            if (key.toLowerCase() === name.toLowerCase()) {
                matchingNames.push(key);
            }
        });
        for (const key of matchingNames) {
            params.delete(key);
        }
        if (value !== null) {
            params.set(name, String(value));
        }
    }
}

function appendQuery(url: string, params: URLSearchParams): string {
    const query = params.toString();
    return query ? `${url}?${query}` : url;
}

export function normalizeArcGISServiceUrl(value: string): string {
    const { url, params } = parseArcGISUrl(value);
    const normalizedParams = new URLSearchParams();
    params.forEach((value, key) => {
        if (key.toLowerCase() !== 'f' || !['json', 'pjson'].includes(value.toLowerCase())) {
            normalizedParams.append(key, value);
        }
    });
    return appendQuery(url, normalizedParams);
}

export function buildArcGISRequestUrl(
    value: string,
    path: string = '',
    parameters: ArcGISRequestParameters = {},
): string {
    const { url, params } = parseArcGISUrl(value);
    setArcGISParameters(params, parameters);
    const endpoint = path ? `${url}/${path.replace(/^\/+/, '')}` : url;
    return appendQuery(endpoint, params);
}

export function createArcGISTileSource(
    value: string,
    parameters: ArcGISRequestParameters = {},
    map?: OLMap,
    metadata: ArcGISMapRenderingMetadata = {},
): TileArcGISRest {
    const { url, params } = parseArcGISUrl(value);
    setArcGISParameters(params, { ...parameters, F: 'image' });
    const imageParams: [string, string][] = [];
    const queryParams = new URLSearchParams();
    params.forEach((value, key) => {
        const name = key.toLowerCase();
        if (IMAGE_PARAMETER_NAMES.has(name)) {
            imageParams.push([key.toUpperCase(), value]);
        } else if (name === 'token' || name === 'projection') {
            imageParams.push([name, value]);
        } else {
            queryParams.append(key, value);
        }
    });

    const options: TileArcGISRestOptions = {
        url,
        params: Object.fromEntries(imageParams),
        crossOrigin: 'anonymous',
    };
    const source = map ? new ViewResolutionArcGISTileSource(options, map, metadata) : new TileArcGISRest(options);
    const query = queryParams.toString();
    if (query) {
        const tileUrlFunction = source.getTileUrlFunction();
        source.setTileUrlFunction((tileCoord, pixelRatio, projection): string | undefined => {
            const requestUrl = tileUrlFunction.call(source, tileCoord, pixelRatio, projection);
            if (!requestUrl) {
                return undefined;
            }
            return `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}${query}`;
        });
    }
    return source;
}

export function shouldUseArcGISViewResolution(metadata: ArcGISMapRenderingMetadata, hasLabels: boolean): boolean {
    return (
        metadata.singleFusedMapCache === false &&
        hasLabels &&
        !!metadata.capabilities?.split(',').some((capability) => capability.trim().toLowerCase() === 'map')
    );
}
